/**
 * Pure lab-marker status classification for the Records status board.
 *
 * A marker with a reference range is graded green/amber/red vs its bounds:
 * out of range is red; inside but within 5% of a bound (the range span when both
 * bounds are known, else 5% of the bound's magnitude) is amber; comfortably
 * inside is green. A marker with no usable range is gray. Status is later paired
 * with an icon + text label in the UI — never conveyed by color alone.
 */

import type { StoredLabPanel } from '../api.js';

export type LabStatus = 'good' | 'amber' | 'red' | 'gray';

/** The 5% margin used for the amber "borderline" band. */
const MARGIN = 0.05;

/**
 * Classify a single value against optional bounds. `gray` when there is nothing
 * to compare (no value or no bound at all).
 */
export function classifyMarker(
  value: number | null,
  refLow: number | null,
  refHigh: number | null,
): LabStatus {
  if (value === null || !Number.isFinite(value)) return 'gray';
  const hasLow = refLow !== null && Number.isFinite(refLow);
  const hasHigh = refHigh !== null && Number.isFinite(refHigh);
  if (!hasLow && !hasHigh) return 'gray';

  // Out of range → red.
  if (hasLow && value < (refLow as number)) return 'red';
  if (hasHigh && value > (refHigh as number)) return 'red';

  // Borderline band: 5% of the range span when both bounds exist, else 5% of the
  // present bound's magnitude.
  const span = hasLow && hasHigh ? (refHigh as number) - (refLow as number) : null;
  if (hasLow) {
    const margin = span !== null ? span * MARGIN : Math.abs(refLow as number) * MARGIN;
    if (value <= (refLow as number) + margin) return 'amber';
  }
  if (hasHigh) {
    const margin = span !== null ? span * MARGIN : Math.abs(refHigh as number) * MARGIN;
    if (value >= (refHigh as number) - margin) return 'amber';
  }
  return 'good';
}

export interface LatestMarker {
  marker: string;
  value: number | null;
  textValue: string | null;
  unit: string | null;
  date: string;
  refLow: number | null;
  refHigh: number | null;
  status: LabStatus;
}

/**
 * The most recent result per marker across all panels, classified. `panels` are
 * expected newest-first (as the API returns them), so the first time a marker is
 * seen is its latest reading. Markers without a numeric value are still listed
 * (as gray) so the board reflects everything on record.
 */
export function latestMarkers(panels: StoredLabPanel[]): LatestMarker[] {
  const seen = new Map<string, LatestMarker>();
  for (const panel of panels) {
    for (const r of panel.results) {
      if (seen.has(r.marker)) continue;
      seen.set(r.marker, {
        marker: r.marker,
        value: r.value,
        textValue: r.textValue,
        unit: r.unit,
        date: panel.panelDate,
        refLow: r.refLow,
        refHigh: r.refHigh,
        status: classifyMarker(r.value, r.refLow, r.refHigh),
      });
    }
  }
  return [...seen.values()];
}

/** Human label + one-glyph indicator for a status (paired with the color). */
export const STATUS_META: Record<LabStatus, { label: string; glyph: string }> = {
  good: { label: 'In range', glyph: '●' },
  amber: { label: 'Borderline', glyph: '▲' },
  red: { label: 'Out of range', glyph: '✕' },
  gray: { label: 'No range', glyph: '–' },
};
